export const common = {
  nav: {
    features: 'Features',
    pricing: 'Pricing',
    login: 'Login',
    getStarted: 'Get Started',
    backToHome: 'Back to Home',
    leadGeneration: 'Lead Generation'
  },
  hero: {
    badge: 'Powered by GPT-5',
    title: 'Your Business on',
    titleHighlight: 'Autopilot',
    titleEnd: 'with Real Artificial Intelligence',
    subtitle: 'Customer Service, Sales, Scheduling and CRM integrated on WhatsApp.',
    subtitleEnd: 'Let AI work while you scale.',
    watchDemo: 'Watch Demo',
    talkToConsultant: 'Talk to Specialist',
    stats: {
      support: 'Active Support',
      reduction: 'Time Reduction',
      conversions: 'More Conversions',
      leads: 'Lost Leads'
    }
  },
  problem: {
    badge: 'The Real Problem',
    title: 'You Are Losing Money',
    titleHighlight: 'Every Day',
    subtitle: 'While you read this, leads are being ignored, sales are being lost and your competition is using AI to dominate the market.',
    items: {
      waiting: {
        title: 'Leads Waiting Too Long',
        description: 'Potential customers abandon the conversation because your team cannot respond fast enough.'
      },
      disorganized: {
        title: 'Disorganized Team',
        description: 'Multiple agents, lost conversations, duplicate information and no real control over the process.'
      },
      followups: {
        title: 'Forgotten Follow-ups',
        description: 'Valuable opportunities slip through your fingers because no one remembered to follow up.'
      },
      chaotic: {
        title: 'Chaotic WhatsApp',
        description: 'Hundreds of mixed conversations, impossible to know who is a hot lead, who already bought or who needs attention.'
      }
    },
    warning: 'Every minute without automation, you lose opportunities',
    warningSubtitle: 'Companies that implement AI in customer service grow 3x faster than the competition.'
  },
  solution: {
    badge: 'The Complete Solution',
    title: 'The First AI Operating System',
    titleHighlight: 'All-in-One for WhatsApp',
    subtitle: 'Clone your best salesperson, automate your CRM and scale your revenue without hiring more employees.',
    aiAgents: {
      title: 'AI Agents',
      description: 'GPT-5 intelligence that understands audio, text and context. Not a dumb button bot - it\'s an AI that really talks.',
      features: {
        context: 'Context understanding',
        natural: 'Natural responses',
        learning: 'Continuous learning'
      }
    },
    voiceAgents: {
      title: 'Voice Agents',
      description: 'Ultra-realistic voice interaction via ElevenLabs integration. Your customers won\'t believe they\'re talking to an AI.',
      features: {
        humanized: 'Humanized voice',
        instant: 'Instant response',
        multilingual: 'Multiple languages'
      }
    },
    crm: {
      title: 'Complete CRM',
      description: 'Kanban view, pipelines and lead tags directly in the chat interface. Everything integrated, no separate tools.',
      features: {
        pipeline: 'Visual pipeline',
        automation: 'Task automation',
        analytics: 'Real-time reports'
      }
    },
    inbox: {
      title: 'Multi-Agent Inbox',
      description: 'One WhatsApp number, multiple human or AI agents working together. Perfect collaboration in real time.',
      features: {
        distribution: 'Smart distribution',
        handoff: 'AI-Human handoff',
        monitoring: 'Real-time monitoring'
      }
    },
    chat: {
      user1: 'Hello! I would like to know more about your services.',
      bot1: 'Hello! I\'m happy to help! We offer complete WhatsApp automation solutions with AI. Can I show you how it works?',
      user2: 'Yes, please! How does AI understand context?',
      bot2: 'Our AI uses GPT-5 to understand not just words, but the complete context of the conversation. It remembers previous interactions and adapts responses to each customer\'s profile. It\'s like having your best salesperson working 24/7!',
      assistant: 'Julia from Automat',
      online: 'online',
      typing: 'Type a message'
    },
    voice: {
      recording: 'recording...',
      assistant: 'Voice AI Assistant'
    }
  },
  features: {
    badge: 'Technical Differentials',
    title: 'Cutting-Edge Technology that',
    titleHighlight: 'Really Works',
    subtitle: 'It\'s not just another tool. It\'s a complete ecosystem built to scale your business.',
    scheduling: {
      title: 'Smart Scheduling',
      description: 'AI accesses your calendar and schedules appointments automatically. No back and forth, no confusion.',
      stats: 'Save 15h/week'
    },
    broadcast: {
      title: 'Broadcast & Remarketing',
      description: 'Mass messages with smart filters to reactivate cold leads and turn them into customers.',
      stats: '40% reactivation'
    },
    sharedInbox: {
      title: 'Shared Inbox',
      description: 'Dashboard for managers to monitor conversations in real time. Total operation visibility.',
      stats: '100% transparency'
    },
    analytics: {
      title: 'Advanced Analytics',
      description: 'Detailed performance metrics, conversion rate and ROI. Decisions based on real data.',
      stats: 'Average ROI of 380%'
    },
    security: {
      title: 'Enterprise Security',
      description: 'End-to-end encryption, GDPR compliant and automatic backup. Your data always protected.',
      stats: '99.9% uptime'
    },
    integrations: {
      title: 'Native Integrations',
      description: 'Connect with your CRM, ERP, calendar and favorite tools. Everything synchronized automatically.',
      stats: '50+ integrations'
    },
    cta: {
      title: 'Ready to Take Off?',
      subtitle: 'Join hundreds of companies that have already automated their customer service and multiplied their results.',
      button: 'Schedule Free Demo'
    }
  },
  kanban: {
    badge: 'Try the Smart Kanban in Practice',
    title: 'CRM with Kanban and Artificial Intelligence',
    subtitle: 'Drag, edit, filter, create and move leads between stages. See the power of our AI Kanban and discover how it can transform your sales management!',
    metrics: {
      totalLeads: 'Total Leads',
      totalValue: 'Total Value',
      conversion: 'Conversion',
      priority: 'Priority',
      inProgress: 'In Progress',
      performance: 'Performance',
      excellent: 'Excellent'
    },
    columns: {
      preQualified: 'Pre-Qualified (AI)',
      initialContact: 'Initial Contact',
      negotiation: 'Negotiation',
      closing: 'Closing'
    },
    leads: {
      qualifiedByAI: 'Qualified by AI',
      aiMonitoring: 'AI Monitoring',
      newLead: 'New lead, awaiting contact',
      warmLead: 'Warm lead, responded to survey',
      awaitingResponse: 'Awaiting response',
      coldLead: 'Cold lead, no response',
      leadInAnalysis: 'Lead in analysis',
      leadResolved: 'Lead resolved',
      conversations: 'conversations',
      broadcasts: 'broadcasts',
      remarketing: 'remarketing',
      automations: 'automations',
      hot: 'Hot',
      warm: 'Warm',
      cold: 'Cold',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      today: 'Today',
      total: 'Total',
      average: 'Average'
    }
  },
  scheduling: {
    badge: 'Automated Scheduling',
    title: 'Smart Scheduling System',
    subtitle: 'Allow your customers to schedule appointments and services 24/7 via WhatsApp',
    chat: {
      user1: 'Hello! I would like to schedule a haircut appointment.',
      bot1: 'Sure! I\'ll help you with the scheduling. What day would you like to schedule?',
      user2: 'Next Wednesday, please.',
      bot2: 'Perfect! We have the following times available on Wednesday (05/15):',
      times: ['09:00', '11:30', '13:00', '14:30', '16:00', '17:30'],
      bot2b: 'Enter the desired time to confirm your appointment.',
      user3: 'I\'ll choose 2:30 PM, please.',
      bot3: 'Great! Your appointment has been confirmed:',
      confirmation: {
        title: 'Appointment Confirmed',
        service: 'Service: Haircut',
        date: 'Date: 05/15/2023 (Wednesday)',
        time: 'Time: 2:30 PM',
        location: 'Location: Visage Hair Studio',
        reminder: 'You will receive a reminder 2 hours before'
      }
    },
    features: {
      publicPage: {
        title: 'Public Scheduling Page',
        description: 'In addition to scheduling via WhatsApp, we offer a personalized public scheduling page for your customers',
        customization: {
          title: 'Total Customization',
          description: 'Custom colors, logo and layout'
        },
        publicAccess: {
          title: 'Public Access',
          description: 'Unique link to share'
        },
        configurable: {
          title: 'Fully Configurable',
          description: 'Hours, services and availability'
        }
      },
      availability: {
        title: '24/7 Availability',
        description: 'Your customers can schedule services at any time of day or night, without human intervention required.'
      },
      configurable: {
        title: 'Fully Configurable',
        description: 'Set available hours, service duration, intervals between appointments and much more according to your needs.'
      },
      reminders: {
        title: 'Automatic Reminders',
        description: 'Reduce no-shows with automatic reminders sent before scheduled appointments, increasing efficiency.'
      },
      control: {
        title: 'Total Control',
        description: 'Easily manage all appointments, reschedule, cancel or confirm directly through the system.'
      }
    },
    cta: 'Start sending now'
  },
  broadcast: {
    badge: 'New Feature',
    title: 'Mass Broadcasts with Intelligence',
    subtitle: 'Reach all your customers with personalized and efficient messages',
    campaign: {
      header: 'Marketing Campaign',
      name: 'Campaign Name',
      nameValue: 'Winter Flash Sale',
      message: 'Personalized Message',
      messageValue: 'Hi, {{name}}! 👋 Take advantage of our special winter promotion with 30% OFF on all products. Valid today only!',
      segmentation: 'Segmentation',
      segment1: 'Active customers in the last 30 days',
      segment2: 'Interested in summer products',
      progress: 'Campaign progress',
      recipients: 'recipients',
      delivered: 'delivered',
      read: 'read'
    },
    features: {
      strategic: {
        title: 'Strategic Reach',
        description: 'Send messages to thousands of contacts with smart segmentation, ensuring the right message reaches the right person.'
      },
      realtime: {
        title: 'Real-Time Metrics',
        description: 'Track your campaign performance in real time with detailed delivery, reading and engagement metrics.'
      },
      automation: {
        title: 'Smart Automation',
        description: 'Set schedules, intervals and specific conditions for sending, optimizing open and response rates.'
      },
      personalization: {
        title: 'Advanced Personalization',
        description: 'Use AI to create personalized messages that resonate with each customer individually, increasing engagement and conversions.'
      }
    },
    cta: 'Start sending now'
  },
  socialProof: {
    badge: 'Proven Results',
    title: 'Numbers Speak for Themselves',
    subtitle: 'Real companies, real results. See the impact of smart automation.',
    metrics: {
      responseTime: {
        value: '90%',
        label: 'Reduction in Response Time',
        description: 'From hours to seconds. Your customers will never wait again.'
      },
      sales: {
        value: '30%',
        label: 'Increase in Sales',
        description: 'More conversions, less effort. AI works 24/7 for you.'
      },
      leads: {
        value: '5x',
        label: 'More Qualified Leads',
        description: 'Smart filtering that identifies who will really buy.'
      },
      responseRate: {
        value: '100%',
        label: 'Response Rate',
        description: 'Zero ignored leads. Every message receives immediate attention.'
      }
    }
  },
  pricing: {
    badge: 'Plans & Pricing',
    title: 'Choose the Ideal Plan',
    titleHighlight: 'For Your Business',
    subtitle: 'Plans designed for each stage of your growth',
    starter: {
      name: 'Starter',
      description: 'For clinics or freelance professionals who want to organize customer service and appointments on WhatsApp.',
      price: 'R$ 597',
      perMonth: '/month',
      setup: 'One-time setup of R$ 497',
      sections: {
        profile: 'Ideal Profile',
        capacity: 'Capacity',
        team: 'Team',
        ai: 'Artificial Intelligence',
        features: 'Features'
      },
      features: {
        profile: 'Clinic or Freelance Professional',
        messages: 'Unlimited messages',
        capacity: '1 calendar / professional',
        users: 'Unlimited users',
        whatsapp: '1 WhatsApp number',
        aiLevel: 'Basic triage (Q&A + scheduling)',
        ocr: 'Guide Reading (OCR): Not included',
        voiceAgent: 'Voice Agent: No (text responses only)',
        recall: 'Reactivation (Recall): Manual'
      },
      cta: 'Get Started'
    },
    pro: {
      badge: '🔥 Best Seller',
      name: 'PRO',
      description: 'Ideal for clinics and professional operations that need to automate customer service, sales, scheduling and intelligent triage via WhatsApp.',
      price: 'R$ 997',
      perMonth: '/month',
      setup: 'One-time setup of R$ 897',
      sections: {
        profile: 'Ideal Profile',
        capacity: 'Capacity',
        team: 'Team',
        ai: 'Artificial Intelligence',
        features: 'Features'
      },
      features: {
        profile: 'Professional operations with active WhatsApp service',
        messages: 'Unlimited messages',
        capacity: 'Up to 5 calendars / professionals',
        capacityExtra: 'Additional calendar: R$ 149/month',
        users: 'Unlimited users',
        whatsapp: 'Up to 2 WhatsApp numbers',
        whatsappExtra: 'Additional number: R$ 149/month',
        humanHandoff: 'Automatic transfer to humans',
        aiLevel: 'Clinical Assistant (exam reading + reasoning)',
        ocr: 'Guide Reading (OCR): Included',
        voiceAgent: 'Voice Agent: Yes',
        recall: 'Reactivation (Recall): Automatic'
      },
      cta: 'Subscribe Now'
    },
    scale: {
      badge: '👑 Premium',
      name: 'Scale',
      description: 'For networks, franchises and high-volume operations that need to scale service and centralize multiple units.',
      price: 'R$ 1,997',
      perMonth: '/month',
      setup: 'One-time setup of R$ 1,897',
      sections: {
        profile: 'Ideal Profile',
        capacity: 'Capacity',
        team: 'Team',
        ai: 'Artificial Intelligence',
        features: 'Features'
      },
      features: {
        profile: 'Networks, franchises or high volume',
        messages: 'Unlimited messages',
        capacity: 'Up to 15 calendars / professionals',
        capacityExtra: 'Additional calendar: R$ 149/month',
        users: 'Unlimited users',
        whatsapp: 'Up to 5 WhatsApp numbers',
        whatsappExtra: 'Additional number: R$ 149/month',
        humanHandoff: 'Automatic transfer to humans',
        aiLevel: 'Clinical Assistant (exam reading + reasoning)',
        ocr: 'Guide Reading (OCR): Included',
        voiceAgent: 'Voice Agent: Yes',
        recall: 'Reactivation (Recall): Automatic'
      },
      cta: 'Scale Now'
    },
    guarantee: {
      title: '7-day guarantee',
      description: 'with full money back.',
      risk: 'Zero Risk.'
    }
  },
  finalCTA: {
    badge: 'Last Chance',
    title: 'Ready to Replace Chaos',
    titleHighlight: 'with Artificial Intelligence?',
    subtitle: 'Start your transformation today. While you hesitate, your competitors are already automating and growing 3x faster.',
    watchDemo: 'Watch Full Demo Now',
    talkToSpecialist: 'Talk to Specialist',
    features: {
      setup: 'Setup without complications',
      guarantee: '7-day guarantee',
      support: 'Dedicated support',
      secure: '100% secure'
    }
  },
  footer: {
    description: 'The first complete AI operating system for WhatsApp. Automate customer service, sales and CRM with cutting-edge artificial intelligence.',
    address: {
      title: 'Address:',
      street: 'Rua Pais Leme, 215, 17th floor, Pinheiros',
      zip: 'ZIP 05424-150',
      city: 'São Paulo - SP, Brazil'
    },
    product: {
      title: 'Product',
      features: 'Features',
      pricing: 'Pricing'
    },
    company: {
      title: 'Company',
      about: 'About Us',
      blog: 'Blog',
      contact: 'Contact'
    },
    legal: {
      title: 'Legal',
      privacy: 'Privacy',
      terms: 'Terms of Use',
      security: 'Security',
      lgpd: 'GDPR'
    },
    copyright: '© 2025 Automat. All rights reserved.'
  },
  about: {
    title: 'About Us',
    subtitle: 'We are a team passionate about technology and client experience, who decided to revolutionize the market through quality service for everyone.',
    mission: {
      title: 'Our Mission',
      p1: 'Democratize access to cutting-edge customer service technology, allowing companies of all sizes to offer exceptional experiences through artificial intelligence.',
      p2: 'We believe that every customer interaction is an opportunity to create meaningful and lasting connections.'
    },
    values: {
      title: 'Our Values',
      passion: {
        title: 'Customer Passion',
        description: 'We put our customers\' success at the center of everything we do.'
      },
      innovation: {
        title: 'Constant Innovation',
        description: 'We always seek the best technological solutions to solve real problems.'
      },
      collaboration: {
        title: 'Collaboration',
        description: 'We work together, as a team, to achieve extraordinary results.'
      }
    }
  },
  inbox: {
    clinic: 'Vitae Clinic',
    agentPanel: 'Agent Panel',
    search: 'Search by name, number...',
    all: 'All',
    pending: 'Pending',
    mine: 'Mine',
    typeMessage: 'Type a message...'
  }
};
